import type {MediaPlayer} from 'amazon-ivs-player';

import {AmazonIVSStateMachine} from '../../analyticsStateMachines/amazonivs/AmazonIVSStateMachine';
import {Analytics} from '../../core/Analytics';
import {HeartbeatService} from '../../core/HeartbeatService';
import {Player} from '../../enums/Player';
import {PlayerSize} from '../../enums/PlayerSize';
import {StreamTypes} from '../../enums/StreamTypes';
import {Feature} from '../../features/Feature';
import {FeatureConfig} from '../../features/FeatureConfig';
import {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {normalizeVideoDuration, PlaybackInfo} from '../../types/PlaybackInfo';
import {isVideoInFullscreen} from '../../utils/documentUtils';
import HTMLVideoElementStatisticsProvider from '../html5/player/HTMLVideoElementStatisticsProvider';
import {InternalAdapter} from '../internal/InternalAdapter';
import {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {AmazonIVSPlayerContext} from './player/AmazonIVSPlayerContext';
import {AmazonIVSPlayerEventListener} from './player/AmazonIVSPlayerEventListener';
import {SourceUrlProvider} from './player/SourceUrlProvider';
import CollectorEvents from './utils/CollectorEventBus';
import {WindowEventListener} from './window/WindowEventListener';

export class AmazonIVSInternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  private listener: AmazonIVSPlayerEventListener;
  private windowEventListener: WindowEventListener;
  private playerStatisticsProvider: HTMLVideoElementStatisticsProvider;

  private readonly player: MediaPlayer;
  private readonly sourceUrlProvider: SourceUrlProvider;

  constructor(player: MediaPlayer, sourceUrlProvider: SourceUrlProvider, opts?: AnalyticsStateMachineOptions) {
    super(opts);
    this.player = player;
    this.sourceUrlProvider = sourceUrlProvider;

    const playingHeartbeatService = new HeartbeatService(() => this.currentTime);
    const stateMachine = new AmazonIVSStateMachine(this.stateMachineCallbacks, playingHeartbeatService, this.opts);
    playingHeartbeatService.setListener(stateMachine);
    this.stateMachine = stateMachine;

    const playerContext = new AmazonIVSPlayerContext(player);
    this.listener = AmazonIVSPlayerEventListener.create(this, playerContext, this.qualityChangeService);

    this.playerStatisticsProvider = new HTMLVideoElementStatisticsProvider(player.getHTMLVideoElement());

    this.windowEventListener = new WindowEventListener(this.windowEventTracker, this, playerContext);
  }

  initialize(_analytics: Analytics): Feature<FeatureConfigContainer, FeatureConfig>[] {
    this.listener.startMonitoring(this.player);
    this.windowEventListener.startMonitoring();
    CollectorEvents.RELEASE.subscribe(() => {
      this.release();
    });
    // we do not support any analytics features on IVS yet
    return [];
  }

  protected override get currentTime(): number {
    return this.player.getPosition();
  }

  override resetSourceRelatedState() {
    this.drmPerformanceInfo = undefined;
    this.playerStatisticsProvider.reset();
  }

  getPlayerVersion = () => this.player.getVersion();
  getPlayerName = () => Player.AMAZON_IVS;
  getPlayerTech = () => 'html5';
  getAutoPlay = () => this.player.isAutoplay();
  getCurrentPlaybackInfo(): PlaybackInfo {
    const videoElement = this.player.getHTMLVideoElement();

    const info: PlaybackInfo = {
      droppedFrames: this.playerStatisticsProvider.getDroppedFrames(),
      isLive: this.player.getDuration() === Infinity,
      isMuted: this.player.isMuted(),
      playerTech: this.getPlayerTech(),
      videoWindowWidth: videoElement.clientWidth,
      videoWindowHeight: videoElement.clientHeight,
      videoPlaybackHeight: videoElement.videoHeight,
      videoPlaybackWidth: videoElement.videoWidth,
      videoBitrate: this.player.getQuality().bitrate,
      videoDuration: normalizeVideoDuration(this.player.getDuration()),
      size: isVideoInFullscreen() ? PlayerSize.Fullscreen : PlayerSize.Window,
      ...this.getVideoAndAudioCodecs(),
      ...this.getCommonPlaybackInfo(),
      streamFormat: StreamTypes.hls,
      m3u8Url: this.sourceUrlProvider.m3u8Url,
    };
    return info;
  }

  private getVideoAndAudioCodecs() {
    const codecs = this.player.getQuality().codecs.split(',');
    if (codecs.length == 2) {
      return {
        videoCodec: codecs[0],
        audioCodec: codecs[1],
      };
    }
    return {};
  }
}
